import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour für den überarbeiteten Bedarfsplan-Kalender
 * (Phase 1–5 des Refactorings, Mai 2026).
 *
 * Die große `STAFFING_MANAGEMENT_ONBOARDING_TOUR` deckt den Workflow
 * (Bedarf anlegen → Schichtzuweisung) ab. Diese kleine Tour erklärt
 * dagegen die *neuen UX-Konzepte*, die im Bedarfsplan-Refactor eingeführt
 * wurden — und ist über einen Help-Button (Pattern analog
 * `AUTO_SUGGEST_HELP_TOUR`) jederzeit abrufbar.
 *
 * Vier Schritte:
 *  1. Tag-Header-Warnung (⚠ rot = unbesetzte Schichten, orange = teilweise)
 *  2. Sammler-Lane "Offene Schichten" oben pro Abteilung
 *  3. Position-Cards mit Coverage-Donut + Warning-Icon
 *  4. Schichtmuster-Slide-Over (Vorlagen sind nicht mehr im Kalender)
 *
 * Trigger ausschließlich manuell, damit der User die Tour bewusst startet
 * und sie nicht den Workflow stört. State-Policy erlaubt beliebiges Replay
 * (Hilfe-Modus, kein Setup-Schritt).
 */
export const STAFFING_BEDARFSPLAN_HELP_TOUR: TourConfig = {
  id: TourFeature.StaffingBedarfsplanHelp,
  version: 1,
  title: 'TUTORIAL.STAFFING_BEDARFSPLAN.TITLE',
  description: 'TUTORIAL.STAFFING_BEDARFSPLAN.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: {
    // D-12 (E1): Personalbedarf → ▦-Muster-Modus der Planung.
    // TourCoordinator navigiert via navigateByUrl → Query-String funktioniert.
    route: '/planner?view=muster',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 60,
  // Klassische Guided Tour (Spotlight + Voice-Over möglich), nicht Help-Mode-
  // Icon-Overlay. `feature-discovery` matcht TourCoordinator.startTour.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'TUTORIAL.STAFFING_BEDARFSPLAN.TITLE',
      steps: [
        {
          id: 'sm-bp-day-header',
          anchor: AnchorId.StaffingCalendar,
          spotlightAnchor: AnchorId.StaffingCalendar,
          title: 'TUTORIAL.STAFFING_BEDARFSPLAN.DAY_HEADER.TITLE',
          content: 'TUTORIAL.STAFFING_BEDARFSPLAN.DAY_HEADER.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'sm-bp-open-shifts-lane',
          // Sammler-Zeile pro Abteilung. Anker existiert schon (Planner-Tour
          // nutzt ihn ebenfalls); im Bedarfsplan wird er von der virtuellen
          // "Offene Schichten"-Resource getragen.
          anchor: AnchorId.OpenRequirementsRow,
          spotlightAnchor: AnchorId.OpenRequirementsRow,
          additionalSpotlightAnchors: [AnchorId.OpenRequirementsLane],
          title: 'TUTORIAL.STAFFING_BEDARFSPLAN.OPEN_SHIFTS.TITLE',
          content: 'TUTORIAL.STAFFING_BEDARFSPLAN.OPEN_SHIFTS.CONTENT',
          placement: 'right',
          showNextButton: true,
        },
        {
          id: 'sm-bp-position-cards',
          // Stats-Bar als robuster Anker für den Spotlight auf die
          // Resource-Spalte. Die einzelnen Cards haben keine eigenen Anker —
          // der Calendar-View deckt sie semantisch alle ab.
          anchor: AnchorId.StaffingStatsBar,
          spotlightAnchor: AnchorId.StaffingCalendarView,
          title: 'TUTORIAL.STAFFING_BEDARFSPLAN.POSITION_CARDS.TITLE',
          content: 'TUTORIAL.STAFFING_BEDARFSPLAN.POSITION_CARDS.CONTENT',
          placement: 'bottom',
          showNextButton: true,
        },
        {
          id: 'sm-bp-templates-slide-over',
          anchor: AnchorId.StaffingTemplatesButton,
          spotlightAnchor: AnchorId.StaffingTemplatesButton,
          title: 'TUTORIAL.STAFFING_BEDARFSPLAN.TEMPLATES.TITLE',
          content: 'TUTORIAL.STAFFING_BEDARFSPLAN.TEMPLATES.CONTENT',
          placement: 'bottom',
          showNextButton: true,
        },
      ],
    },
  ],
};
